import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface ItemFilters {
  category_id?: number;
  manufacturer_id?: number;
  packaging_id?: number;
  nature?: string;
  item_master_name?: string;
  product_code?: string;
  limit?: number;
  offset?: number;
  search?: string;
  name?: string;
  code?: string;
  is_deleted?: boolean;
}

export interface ItemApiResponse {
  data: {
    total?: number;
    items?: any[];
    item_masters?: any[];
  };
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ItemService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Search/Get all items with optional filters
   * Endpoint: GET /api/v1/item-masters/
   * Query Parameters: limit, offset, search, name, code, is_deleted
   */
  searchItems(filters?: ItemFilters): Observable<ItemApiResponse> {
    let params = new HttpParams();

    if (filters) {
      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      } else {
        // Default limit for search/autocomplete
        params = params.set('limit', '20');
      }
      
      // Only set category_id if explicitly provided (including 0)
      // If 0 or not provided, don't set it so backend doesn't filter by category
      if (filters.category_id !== undefined && filters.category_id !== null) {
        params = params.set('category_id', filters.category_id.toString());
      }
      
      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }

      // Add search/filter parameters
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.name) {
        params = params.set('name', filters.name);
      }
      if (filters.code) {
        params = params.set('code', filters.code);
      }
      // Default to false if not specified
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', filters.is_deleted.toString());
      } else {
        params = params.set('is_deleted', 'false');
      }
    } else {
      // Default limit for autocomplete
      params = params.set('limit', '20');
      params = params.set('is_deleted', 'false');
    }

    return this.http.get<ItemApiResponse>(`${this.API_BASE_URL}/api/v1/items/`, { 
      params,
      withCredentials: true 
    });
  }

  getItemsList(filters?: ItemFilters): Observable<any> {
    let params = new HttpParams();

    if (filters) {
      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      } else {
        // Default limit for search/autocomplete
        params = params.set('limit', '20');
      }
      
      if (filters.category_id) {
        params = params.set('category_id', filters.category_id.toString());
      }
      
      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }

      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.name || filters.item_master_name) {
        params = params.set('name', filters.item_master_name || filters.name || '');
      }
      if (filters.product_code) {
        params = params.set('product_code', filters.product_code);
      }
      if (filters.manufacturer_id) {
        params = params.set('manufacturer_id', filters.manufacturer_id.toString());
      }
      if (filters.packaging_id) {
        params = params.set('packaging_id', filters.packaging_id.toString());
      }
      if (filters.nature) {
        params = params.set('nature', filters.nature);
      }
      // Default to false if not specified
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', filters.is_deleted.toString());
      } else {
        params = params.set('is_deleted', 'false');
      }
    } else {
      // Default limit for autocomplete
      params = params.set('limit', '20');
      params = params.set('is_deleted', 'false');
    }

    return this.http.get<ItemApiResponse>(`${this.API_BASE_URL}/api/v1/items/`, { 
      params,
      withCredentials: true 
    });
  }

  /**
   * Get single item by ID
   * Endpoint: GET /api/v1/item-masters/{itemId}
   */
  getItemById(itemId: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/item-masters/${itemId}`, {
      withCredentials: true
    });
  }

  /**
   * Get single inventory item by ID
   * Endpoint: GET /api/v1/items/{itemId}
   */
  getInventoryItemById(itemId: string | number, itemTypeId: number = 1): Observable<any> {
    let params = new HttpParams();
    params = params.set('category_id', itemTypeId.toString()); // Inventory items
    
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/items/${itemId}`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Create a new item master
   * Endpoint: POST /api/v1/items/
   */
  createInventoryItem(itemData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/items/`, itemData, {
      withCredentials: true
    });
  }

  /**
   * Update an existing item master
   * Endpoint: PUT /api/v1/items/{itemId}
   */
  updateInventoryItem(itemId: string | number, itemData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/items/${itemId}`, itemData, {
      withCredentials: true
    });
  }

  /**
   * Get audit logs for an item master
   * Endpoint: GET /api/v1/item-masters/:id/audit-logs
   * Query parameters: limit, offset, date, user_id
   */
  getAuditLogs(itemMasterId: string | number, filterParams?: any): Observable<any> {
    let params = new HttpParams();
    
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
          params = params.set(key, filterParams[key].toString());
        }
      });
    }
    
    const url = `${this.API_BASE_URL}/api/v1/items/${itemMasterId}/audit-logs`;
    return this.http.get<any>(url, { 
      params,
      withCredentials: true 
    });
  }

  /**
   * Delete an item master
   * Endpoint: DELETE /api/v1/items/{itemId}
   */
  deleteItem(itemId: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/items/${itemId}`, {
      withCredentials: true
    });
  }
}